import { filterTypeLabelKey } from "@/components/data-table/utils/activeFilterDescriptors";
import { FilterType } from "@/lib/filters";
import { cn } from "@/lib/utils";
import {
  Equal,
  EqualNot,
  Minus,
  Plus,
  SearchCheck,
  SearchX,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PiGreaterThan,
  PiGreaterThanOrEqual,
  PiLessThan,
  PiLessThanOrEqual,
} from "react-icons/pi";

interface FilterTypeOptionsProps {
  filterType: FilterType;
  onChange: (filterType: FilterType) => void;
  className?: string;
}

interface FilterOption {
  value: FilterType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const FILTER_TYPE_ICONS: Record<
  FilterType,
  React.ComponentType<{ className?: string }>
> = {
  [FilterType.IncludesString]: SearchCheck,
  [FilterType.ExcludesString]: SearchX,
  [FilterType.EqualsString]: Equal,
  [FilterType.NotEquals]: EqualNot,
  [FilterType.LessThan]: PiLessThan,
  [FilterType.GreaterThan]: PiGreaterThan,
  [FilterType.LessThanOrEqual]: PiLessThanOrEqual,
  [FilterType.GreaterThanOrEqual]: PiGreaterThanOrEqual,
  [FilterType.IsEmpty]: Minus,
  [FilterType.IsNotEmpty]: Plus,
};

interface FilterSeparator {
  separator: true;
}

type FilterOptionItem = FilterOption | FilterSeparator;

function isSeparator(item: FilterOptionItem): item is FilterSeparator {
  return "separator" in item && item.separator === true;
}

export function FilterTypeOptions({
  filterType,
  onChange,
  className,
}: FilterTypeOptionsProps) {
  const { t } = useTranslation();

  const filterOptions = useMemo(
    (): FilterOptionItem[] => [
      {
        value: FilterType.IncludesString,
        label: t(filterTypeLabelKey(FilterType.IncludesString)),
        icon: SearchCheck,
      },
      {
        value: FilterType.ExcludesString,
        label: t(filterTypeLabelKey(FilterType.ExcludesString)),
        icon: SearchX,
      },
      {
        separator: true,
      },
      {
        value: FilterType.EqualsString,
        label: t(filterTypeLabelKey(FilterType.EqualsString)),
        icon: Equal,
      },
      {
        value: FilterType.NotEquals,
        label: t(filterTypeLabelKey(FilterType.NotEquals)),
        icon: EqualNot,
      },
      {
        value: FilterType.LessThan,
        label: t(filterTypeLabelKey(FilterType.LessThan)),
        icon: PiLessThan,
      },
      {
        value: FilterType.GreaterThan,
        label: t(filterTypeLabelKey(FilterType.GreaterThan)),
        icon: PiGreaterThan,
      },
      {
        value: FilterType.LessThanOrEqual,
        label: t(filterTypeLabelKey(FilterType.LessThanOrEqual)),
        icon: PiLessThanOrEqual,
      },
      {
        value: FilterType.GreaterThanOrEqual,
        label: t(filterTypeLabelKey(FilterType.GreaterThanOrEqual)),
        icon: PiGreaterThanOrEqual,
      },
      {
        separator: true,
      },
      {
        value: FilterType.IsEmpty,
        label: t(filterTypeLabelKey(FilterType.IsEmpty)),
        icon: Minus,
      },
      {
        value: FilterType.IsNotEmpty,
        label: t(filterTypeLabelKey(FilterType.IsNotEmpty)),
        icon: Plus,
      },
    ],
    [t],
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {filterOptions.map((option, index) =>
        isSeparator(option) ? (
          <div key={index} className="h-px bg-border my-1" />
        ) : (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors",
              filterType === option.value && "bg-accent text-accent-foreground",
            )}
          >
            <option.icon className="h-4 w-4" />
            {option.label}
          </button>
        ),
      )}
    </div>
  );
}
